import * as React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import * as Sentry from "@sentry/react";

import { ThemeProvider } from "@mui/material/styles";

import getUserContext from "util/GetUserContext";
import { analytics, PostHogProvider, SegmentProvider } from "services/analytics";
import { installSegmentQueue } from "segment";
import GoogleAuthGate from "components/common/GoogleAuthGate";

import App from "./app";

import { Theme } from "./Theme";
import configureStore from "./store";

import "./index.css";
import { scrubSentryBreadcrumb, scrubSentryEvent } from "util/sentryScrub";
import { captureLinkTokenEarly } from "plg/utils/sensitiveUrl";
import { loadPlgSelfServe } from "hooks/usePlgSelfServe";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("App Error:", error, errorInfo);
  }

  render(): React.ReactNode {
    const { hasError, error } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <h1>Something went wrong.</h1>
          <p>Please refresh the page or contact support if the issue persists.</p>
          {error && <p className="error-details">{error.message}</p>}
        </div>
      );
    }

    return children;
  }
}

// Create store outside the init function
const store = configureStore();

// Defined Root component outside the initApp function
function Root(): React.ReactElement {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ThemeProvider theme={Theme}>
          <BrowserRouter>
            <GoogleAuthGate>
              <App />
            </GoogleAuthGate>
          </BrowserRouter>
        </ThemeProvider>
      </Provider>
    </ErrorBoundary>
  );
}

const appHostName = window.location.hostname.split(".")[0];

let environment = "";
let clientSideIDLaunchDarkly = "";

if (appHostName === "app") {
  environment = "prod";
  clientSideIDLaunchDarkly = "66560e84484b7b0fef5cbb73";
} else if (appHostName === "uat") {
  environment = "uat";
  clientSideIDLaunchDarkly = "66c4781bc1e1e41080a55b1e";
} else {
  environment = "qa";
  clientSideIDLaunchDarkly = "66c477f03f8d3a0fa9cef9fb";
}

// Before analytics and Sentry (incl. Replay) see the URL: keep a magic-link ?link= in memory only.
captureLinkTokenEarly();

// Queue Segment calls from the first render; the script loads once the platform config names the environment.
installSegmentQueue();
analytics.registerProvider(new PostHogProvider());
analytics.registerProvider(new SegmentProvider());

if (window.location.hostname !== "localhost") {
  Sentry.init({
    dsn: "https://a84400b0b0c3585291fe7c646486031a@o4506113118633984.ingest.us.sentry.io/4506828857278464",
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    environment,
    tracesSampleRate: 1.0,
    tracePropagationTargets: ["*"],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Magic-link tokens (?token=…) must never be sent to Sentry.
    beforeSend: (event) => scrubSentryEvent(event),
    beforeSendTransaction: (event) => scrubSentryEvent(event),
    beforeBreadcrumb: (breadcrumb) => scrubSentryBreadcrumb(breadcrumb),
  });
}

const initApp = async (): Promise<void> => {
  try {
    const rootElement = document.getElementById("root");
    if (!rootElement) throw new Error("Failed to find the root element");

    // Routes are built once on first render, so the PLG switch must be known before it.
    const [LDProvider] = await Promise.all([
      asyncWithLDProvider({
        clientSideID: clientSideIDLaunchDarkly,
        context: getUserContext(),
        options: {
          bootstrap: "localStorage",
        },
      }),
      loadPlgSelfServe(),
    ]);

    const root = createRoot(rootElement);

    root.render(
      <ErrorBoundary>
        <LDProvider>
          <Root />
        </LDProvider>
      </ErrorBoundary>
    );
  } catch (error) {
    console.error("Failed to initialize app:", error);
    const rootElement = document.getElementById("root");
    if (rootElement) {
      const root = createRoot(rootElement);
      root.render(
        <div style={{ padding: "20px", textAlign: "center" }}>
          <h1>Failed to load the application</h1>
          <p>Please refresh the page or contact support if the issue persists.</p>
        </div>
      );
    }
  }
};

initApp();
